import { assert } from '@datapad/utilities';
import { useTree } from '@fluidframework/react/alpha';
import { Tree } from 'fluid-framework';
import React from 'react';
import { CampaignEvent, GalaxyEvent, } from './calendar/index.js';
import { Player } from './User.js';
export const AppContext = React.createContext(undefined);
export function useAppContextProvider(dataModel, userContext, options) {
    useTree(dataModel);
    const effectivePermissionLevel = userContext.permissionLevel === 'game-master' && options.effectivePermissionLevel
        ? options.effectivePermissionLevel
        : userContext.permissionLevel;
    const contextBase = {
        ...userContext,
        getEffectivePermissionLevel: () => effectivePermissionLevel,
        getUser: (ref) => dataModel.getUser(ref),
        getAllUsers: () => [...dataModel.users.values()],
        getCharacter: (ref) => dataModel.getCharacter(ref),
        getUsersCharacters: (specifiedUser) => {
            if (userContext.permissionLevel === 'guest' || effectivePermissionLevel === 'guest') {
                return [];
            }
            if (userContext.permissionLevel === 'game-master' ||
                effectivePermissionLevel === 'game-master') {
                return [];
            }
            let user = specifiedUser;
            if (user === undefined) {
                user = userContext.user;
            }
            if (effectivePermissionLevel === 'player') {
                const resolvedUser = dataModel.getUser(user);
                assert(Tree.is(resolvedUser, Player), `Expected user to be a Player, but was ${Tree.schema(user).identifier}.`);
                return dataModel.getPlayersCharacters(resolvedUser);
            }
            return [];
        },
        getAllCharacters: () => dataModel.getAllCharacters(),
        getCampaign: (ref) => dataModel.getCampaign(ref),
        getAllCampaigns: () => [...dataModel.campaigns.values()],
        handleCharacterLink: options.handleCharacterLink,
        handleCalendarLink: options.handleCalendarLink,
        getVisibleEvents: () => filterEventsByPermission([...dataModel.calendarEvents.values()], effectivePermissionLevel, userContext.permissionLevel === 'guest' || effectivePermissionLevel === 'guest'
            ? undefined
            : userContext.character, dataModel),
        getVisibleBirthdays: () => filterBirthdaysByPermission([...dataModel.characters.values()], effectivePermissionLevel, userContext.permissionLevel !== 'guest' ? userContext.character : undefined),
    };
    if (userContext.permissionLevel === 'game-master') {
        return {
            ...contextBase,
            setEffectivePermissionLevel: (level) => options.setEffectivePermissionLevel?.(level),
            addCampaign: (campaign) => dataModel.campaigns.set(campaign.id, campaign),
            deleteCampaign: (campaign) => dataModel.deleteCampaign(campaign),
            addCharacter: (character) => dataModel.characters.set(character.id, character),
            deleteCharacter: (character) => dataModel.deleteCharacter(character),
            addEvent: (event) => dataModel.calendarEvents.set(event.id, event),
            deleteEvent: (event) => dataModel.deleteEvent(event),
            addPlayer: (player) => dataModel.users.set(player.id, player),
            deletePlayer: (player) => dataModel.deletePlayer(player),
        };
    }
    else {
        return contextBase;
    }
}
export function useAppContext() {
    const context = React.useContext(AppContext);
    if (!context) {
        throw new Error('AppContext not provided.');
    }
    return context;
}
function filterEventsByPermission(events, permissionLevel, characterRef, dataModel) {
    if (permissionLevel === 'game-master') {
        return events;
    }
    if (permissionLevel === 'guest') {
        return events.filter((event) => Tree.is(event, GalaxyEvent));
    }
    if (characterRef === undefined) {
        return events.filter((event) => Tree.is(event, GalaxyEvent));
    }
    return events.filter((event) => {
        if (Tree.is(event, GalaxyEvent)) {
            return true;
        }
        if (event.characters.some((ref) => ref.id === characterRef.id)) {
            return true;
        }
        if (Tree.is(event, CampaignEvent)) {
            const campaign = dataModel.getCampaign(event.campaign);
            return isCharacterInCampaign(characterRef, campaign);
        }
        return false;
    });
}
function isCharacterInCampaign(characterRef, campaign) {
    return [...campaign.activeCharacters, ...campaign.inactiveCharacters].some((ref) => ref.id === characterRef.id);
}
function filterBirthdaysByPermission(characters, permissionLevel, characterRef) {
    const withBirthdays = characters.filter((c) => c.birthday !== undefined);
    if (permissionLevel === 'game-master') {
        return withBirthdays;
    }
    if (permissionLevel === 'guest') {
        return withBirthdays.filter((c) => c.famous);
    }
    if (characterRef === undefined) {
        return withBirthdays.filter((c) => c.famous);
    }
    return withBirthdays.filter((c) => {
        if (c.famous) {
            return true;
        }
        return c.knownBy.some((ref) => ref.id === characterRef.id);
    });
}
//# sourceMappingURL=AppContext.js.map